import { getPageImage } from "@/lib/page-imagery";
const isoPhoto = getPageImage("/services", "iso-group");
const registrationPhoto = getPageImage("/services", "registration-group");
import { FaqSection } from "./FaqSection";
import { LangLink } from "./LangLink";
import { content } from "@/lib/content";
import { locations } from "@/lib/locations";
import { useLanguage } from "@/lib/language";
import { BuyerGuides, ResponsibilityNote } from "./BuyerGuides";

const summaries: Record<
  string,
  { name: { en: string; ar: string }; line: { en: string; ar: string } }
> = {
  "iso-9001": {
    name: { en: "Quality management", ar: "إدارة الجودة" },
    line: {
      en: "Prepare your processes, responsibilities and quality records.",
      ar: "جهّز عملياتك ومسؤوليات الفريق وسجلات الجودة.",
    },
  },
  "iso-14001": {
    name: { en: "Environmental management", ar: "الإدارة البيئية" },
    line: {
      en: "Organize environmental risks, controls and evidence.",
      ar: "نظّم المخاطر البيئية وضوابطها والأدلة الداعمة.",
    },
  },
  "iso-45001": {
    name: { en: "Workplace health and safety", ar: "الصحة والسلامة المهنية" },
    line: {
      en: "Prepare the system for managing workplace risks.",
      ar: "جهّز نظام إدارة مخاطر الصحة والسلامة في العمل.",
    },
  },
  "iso-22000": {
    name: { en: "Food safety management", ar: "إدارة سلامة الغذاء" },
    line: {
      en: "Prepare food-safety processes, controls and records.",
      ar: "جهّز عمليات سلامة الغذاء وضوابطها وسجلاتها.",
    },
  },
  "iso-27001": {
    name: { en: "Information security", ar: "أمن المعلومات" },
    line: {
      en: "Prepare your information-security management system.",
      ar: "جهّز نظام إدارة أمن المعلومات في مؤسستك.",
    },
  },
  "iso-29001": {
    name: { en: "Oil and gas quality", ar: "الجودة في النفط والغاز" },
    line: {
      en: "Prepare a quality system for petroleum-sector supply activities.",
      ar: "جهّز نظام جودة لأنشطة التوريد في قطاع النفط والغاز.",
    },
  },
  "ministry-of-oil-vendor-registration": {
    name: { en: "Ministry of Oil registration", ar: "التسجيل لدى وزارة النفط" },
    line: {
      en: "Review your company file against the named authority’s request.",
      ar: "راجع ملف شركتك وفق طلب الجهة المعنية.",
    },
  },
  "oil-gas-registration": {
    name: { en: "Oil and gas supplier registration", ar: "تسجيل موردي النفط والغاز" },
    line: {
      en: "Prepare documents for a specific buyer and supplier category.",
      ar: "جهّز الوثائق لجهة مشترية وفئة توريد محددتين.",
    },
  },
};
export function ServicesContent() {
  const { t } = useLanguage();
  const iso = content.services.items.filter((item) => item.slug.startsWith("iso-"));
  const registration = content.services.items.filter((item) => !item.slug.startsWith("iso-"));
  return (
    <>
      <div data-service-catalog className="ic-premium-catalog">
        <section className="ic-section ic-services-intro" aria-labelledby="services-heading">
          <div className="ic-intro-copy">
            <p className="ic-eyebrow">{t({ en: "Work with your team", ar: "نعمل مع فريقك" })}</p>
            <h1 id="services-heading" className="ic-title">
              {t({
                en: "ISO preparation and supplier registration.",
                ar: "التحضير للأيزو وتسجيل الموردين.",
              })}
            </h1>
            <p className="ic-intro">
              {t({
                en: "Build the management system your work needs. Prepare the evidence your buyer asks for. Start with your activity and requirement.",
                ar: "طوّر نظام إدارة يناسب عملك، وجهّز الأدلة التي تطلبها الجهة المشترية. نبدأ بنشاط شركتك ومتطلبها الفعلي.",
              })}
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <LangLink
                to="/insights/iso-certification-cost-and-process-in-iraq"
                className="ic-button"
              >
                {t({ en: "ISO cost and preparation process", ar: "تكلفة الأيزو وخطوات التحضير" })}
              </LangLink>
              <LangLink to="/services/oil-gas-registration" className="ic-text-link">
                {t({ en: "Supplier registration", ar: "تسجيل الموردين" })}
              </LangLink>
            </div>
          </div>
        </section>
        <section id="iso-preparation" className="ic-section">
          <div className="ic-group-header">
            <div className="ic-section-heading">
              <p className="ic-eyebrow">
                {t({ en: "01 / Management systems", ar: "01 / أنظمة الإدارة" })}
              </p>
              <h2>
                {t({
                  en: "ISO preparation",
                  ar: "التحضير للأيزو",
                })}
              </h2>
              <p>
                {t({
                  en: "The right scope depends on your activities, sites and buyer requirement. You do not need every standard in this list.",
                  ar: "يتحدد النطاق المناسب بحسب أنشطتك ومواقعك ومتطلب الجهة المشترية. لست بحاجة إلى جميع المواصفات في هذه القائمة.",
                })}
              </p>
            </div>
            <figure className="ic-group-photo">
              <img
                src={isoPhoto.src}
                width={isoPhoto.width}
                height={isoPhoto.height}
                loading="lazy"
                decoding="async"
                style={{ objectPosition: isoPhoto.objectPosition }}
                alt={t(isoPhoto.alt)}
              />
            </figure>
          </div>
          <div className="ic-service-grid">
            {iso.map((item) => (
              <LangLink
                key={item.slug}
                to={`/services/${item.slug}`}
                className="ic-service-card ic-compact-service"
              >
                <div className="ic-card-top">
                  <bdi dir="ltr" className="ic-code">
                    {item.label}
                  </bdi>
                </div>
                <h3>{t(summaries[item.slug].name)}</h3>
                <p>{t(summaries[item.slug].line)}</p>
                <span className="ic-text-link">
                  {t({ en: "Explore service", ar: "تعرّف على الخدمة" })}
                </span>
              </LangLink>
            ))}
          </div>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {t({
              en: "ISO 29001 is outside AQSR’s disclosed UAF-accredited scope. Confirm the proposed issuing body and its accreditation scope before booking.",
              ar: "يقع ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR. تحقق من الجهة المانحة المقترحة ونطاق اعتمادها قبل الحجز.",
            })}
          </p>
        </section>
        <section id="supplier-registration" className="ic-surface">
          <div className="ic-section">
            <div className="ic-group-header">
              <div className="ic-section-heading">
                <p className="ic-eyebrow">
                  {t({ en: "02 / Supplier documents", ar: "02 / وثائق الموردين" })}
                </p>
                <h2>
                  {t({
                    en: "Supplier registration",
                    ar: "تسجيل الموردين",
                  })}
                </h2>
                <p>
                  {t({
                    en: "Identify the buyer, contracting entity and supplier category before assembling a submission. Registration, qualification and contract award are separate decisions.",
                    ar: "حدد الجهة المشترية والكيان المتعاقد وفئة التوريد قبل إعداد طلبك. التسجيل والتأهيل وإحالة العقد قرارات منفصلة.",
                  })}
                </p>
              </div>
              <figure className="ic-group-photo">
                <img
                  src={registrationPhoto.src}
                  width={registrationPhoto.width}
                  height={registrationPhoto.height}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: registrationPhoto.objectPosition }}
                  alt={t(registrationPhoto.alt)}
                />
              </figure>
            </div>
            <div className="ic-service-grid ic-two-columns">
              {registration.map((item) => (
                <LangLink
                  key={item.slug}
                  to={`/services/${item.slug}`}
                  className="ic-service-card ic-compact-service"
                >
                  <h3>{t(summaries[item.slug].name)}</h3>
                  <p>{t(summaries[item.slug].line)}</p>
                  <span className="ic-text-link">
                    {t({ en: "Explore service", ar: "تعرّف على الخدمة" })}
                  </span>
                </LangLink>
              ))}
            </div>
          </div>
        </section>
      </div>
      <p className="ic-imagery-note ic-section-note">
        {t({ en: "Illustrative work scenes.", ar: "صور توضيحية لطبيعة العمل." })}
      </p>
      <ResponsibilityNote />
      <BuyerGuides />
      <section className="ic-section">
        <div className="ic-section-heading">
          <h2>{t({ en: "Support by location", ar: "الدعم بحسب الموقع" })}</h2>
          <p>
            {t({
              en: "Based in Erbil, with remote support and agreed client-site visits across Iraq. Review each market’s scope before proceeding.",
              ar: "نعمل من أربيل ونقدّم الدعم عن بُعد وزيارات متفقاً عليها لمواقع العملاء في العراق. راجع نطاق الدعم في كل سوق قبل المتابعة.",
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {locations.map((loc) => (
            <LangLink
              key={loc.slug}
              to={`/services/iso-certification/${loc.slug}`}
              className="ic-location-link"
            >
              {t(loc.city)}
            </LangLink>
          ))}
        </div>
      </section>
      <FaqSection items={content.faq.servicesHub} />
      <section className="ic-surface">
        <div className="ic-section ic-closing">
          <div>
            <h2>{t({ en: "Not sure where to start?", ar: "لست متأكداً من نقطة البداية؟" })}</h2>
            <p>
              {t({
                en: "Tell us what your customer asked for. We can review the requirement before planning the work.",
                ar: "أخبرنا بما طلبه العميل، لنراجع المتطلب قبل تخطيط العمل.",
              })}
            </p>
          </div>
          <LangLink to="/contact" className="ic-button">
            {t({ en: "Discuss your requirement", ar: "ناقش متطلبك" })}
          </LangLink>
        </div>
      </section>
    </>
  );
}
